// Promise wrapper around the search worker.

import type { CastPlan, CastPlanRequest } from './castPlan';
import type { SearchRequest, SearchResult } from './search';
import type { WorkerRequest, WorkerResponse } from './worker';

const worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });

let nextRequestId = 1;
const pending = new Map<number, (response: WorkerResponse) => void>();

worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
  const resolve = pending.get(event.data.id);
  if (!resolve) return;
  pending.delete(event.data.id);
  resolve(event.data);
};

const ask = (message: WorkerRequest): Promise<WorkerResponse> =>
  new Promise((resolve) => {
    pending.set(message.id, resolve);
    worker.postMessage(message);
  });

/** The best move and its score. */
export const requestSearch = async (request: SearchRequest): Promise<SearchResult> => {
  const response = await ask({ id: nextRequestId++, kind: 'search', request });
  if (response.kind !== 'search') throw new Error('The worker gave no search result');
  return response.result;
};

/** The cast to make now, or null for none. See `castPlan.ts`. */
export const requestCastPlan = async (request: CastPlanRequest): Promise<CastPlan | null> => {
  const response = await ask({ id: nextRequestId++, kind: 'cast', request });
  if (response.kind !== 'cast') throw new Error('The worker gave no plan of a cast');
  return response.result;
};
